<script setup lang="ts">
import type { ContentProductPackage } from '~/models/Content/ContentProductPackage'
import type { ContentProductHeader } from '~/models/Content/ContentProductHeader'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { ContentFolder } from '~/models/Content/ContentFolder'
import { computed, watch } from 'vue'
import { createReusableTemplate } from '@vueuse/core'
import useFilterStore from '~/stores/filter'
import { PendoFeature } from '~/models/Pendo'
import { useStaticProduct } from '~/composables/useStaticProduct'
import { useHeaderChildrenQuery } from '~/composables/useHeader'
import { useContentHelper } from '~/composables/useContentHelper'
import useAccordion from '~/composables/useAccordion'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import HeaderPreviewAccordion from '~/components/subject/HeaderPreviewAccordion.vue'
import DocumentList from '~/components/subject/DocumentList.vue'
import SimpleCard from '~/components/cards/SimpleCard.vue'
import GridCard from '~/components/cards/GridCard.vue'

const {
  header,
  section,
  index = 0,
  hash = '#',
  gradeFilter = true,
  isCollapsible = false,
  chooseTask = false
} = defineProps<{
  header: ContentProductHeader | ContentProductPackage | ContentFolder
  section: ContentPackage
  index?: number
  hash?: string
  gradeFilter?: boolean
  isCollapsible?: boolean
  chooseTask?: boolean
}>()

const emit = defineEmits<{ openAccordion: [locationId: number[]] }>()

const selectionMap = defineModel<Record<number, number>>('selectionMap', { default: {} })

const { gradeFilterFunc } = useFilterStore()
const { isContentHeader } = useContentHelper()
const { Terminprover } = useStaticProduct()
const { addAccordion } = useAccordion()
const [DefineCardList, ReuseCardList] = createReusableTemplate()

const children = computed(() => {
  if (!data.value)
    return []
  return gradeFilter ? data.value.filter(gradeFilterFunc) : data.value
})

const defaultOpen = computed(() => `#${index + 1}` === hash)
const isGrid = computed(() => isContentHeader(section) && section.viewMode === 'grid')
const isFileList = computed(() => isContentHeader(section) && section.viewMode === 'filelist')
const isTaskViewMode = computed(() => isContentHeader(section) && section.viewMode === 'task')
const isCompactViewMode = computed(() => isContentHeader(section) && section.viewMode === 'compact')

const { data, isLoading } = useHeaderChildrenQuery(computed(() => section.mainLocationId), {
  sortField: computed(() => section.sortField),
  sortOrder: computed(() => section.sortOrder),
})

const tracking = computed(() => header.pathString.startsWith(Terminprover.aunivers.pathString!)
  ? { 'data-pendo': PendoFeature.TermExam.NavigateTask }
  : {}
)

watch(children, () => {
  if (!children.value.length || !chooseTask)
    return
  selectionMap.value[section.mainLocationId] ??= children.value[0].locationId
}, { immediate: true })
</script>

<template>
  <DefineCardList>
    <DocumentList
      v-if="isFileList"
      :title="section.title"
      :children="children"
    />
    <ul
      v-else
      class="xs:grid-cols-2 grid auto-rows-fr md:grid-cols-3"
      :class="{
        'gap-6': isGrid,
        'gap-[1px] lg:grid-cols-4': !isGrid,
        'pt-[1px]': isCollapsible,
        'sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6': isTaskViewMode,
        'sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8': isCompactViewMode,
      }"
    >
      <li
        v-for="item in children"
        :key="item.locationId"
        v-bind="tracking"
        class="flex"
        :class="isGrid && 'h-fit'"
      >
        <GridCard
          v-if="isGrid"
          :resource="item"
          :loading="index === 0 ? 'eager' : 'lazy'"
          @click="addAccordion(header.locationId, section.locationId)"
        />
        <SimpleCard
          v-else
          :resource="item"
          :no-image="isTaskViewMode || isCompactViewMode"
          :choose-task="children.length > 1 && chooseTask"
          :selected-id="selectionMap[section.mainLocationId!]"
          @click="addAccordion(header.locationId, section.locationId)"
          @choose="(id) => selectionMap[section.mainLocationId!] = id"
        />
      </li>
    </ul>
  </DefineCardList>

  <AuSkeletonWrapper
    v-if="isLoading"
    class="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    :class="{ 'my-6': isCollapsible }"
  >
    <AuSkeleton
      v-for="n in 6"
      :key="n"
      height="auto"
      class="aspect-video"
      border-radius="0px"
      background="var(--theme-5)"
    />
  </AuSkeletonWrapper>

  <HeaderPreviewAccordion
    v-else-if="isCollapsible"
    :id="`${index + 1}`"
    :header="header"
    :section="section"
    :open="defaultOpen"
    title-style="font-tiempos text-4xl"
    @open-accordion="emit('openAccordion', $event)"
  >
    <ReuseCardList />
  </HeaderPreviewAccordion>
  <div
    v-else
    class="flex flex-col gap-4"
  >
    <h2
      v-if="children.length > 0 && !isFileList"
      class="font-tiempos text-4xl"
      v-text="section.title"
    />
    <div v-if="section.intro && !isFileList" class="max-w-prose pb-2 text-xl font-semibold">
      <RichTextRenderer :text="section.intro" />
    </div>
    <ReuseCardList />
  </div>
</template>
